import React, { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useFormik } from "formik";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import { OptionType } from "../../../../../../shared/components/form-builder/form-config.model";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useParams, useNavigate } from "react-router-dom";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { ActionButtonConfig } from "../../../../../../shared/components/modals/action-popup";
import AlertPopup from "../../../../../../shared/components/modals/alert-moadal";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import useScrollToError from "../../../../../../shared/hooks/useScrollToError";
import {
  IProductCategoryInformation,
  IProductCategoryApiResponse,
  mapProductCategoryPayload,
  initialProductCategoryInformation,
} from "../../../sc-models/product-category-information-dto";
import {
  IProductDetailInformation,
  IProductDetailApiResponse,
  mapProductDetailPayload,
  initialProductDetailInformation,
} from "../../../sc-models/product-detail-information-dto";
import {
  buildProductCategoryValidationSchema,
  validateProductCategoryRow,
  buildProductDetailValidationSchema,
  validateProductDetailRow,
} from "../../utils/scValidation";

interface ProductCategoryInformationTabProps {
  onNextTab?: () => Promise<void>;
  scStatus?: boolean;
}

const ProductCategoryInformationTab: React.FC<ProductCategoryInformationTabProps> = ({ onNextTab, scStatus }) => {
  const { formtype, SC_ID } = useParams();
  const navigate = useNavigate();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const isReadOnlyBasedOnStatus = hasSubmitSC || scStatus;
  const { notify } = useNotification();
  const { setRestrictPopUpSave, isRestrictPopUpSave } = useTransactionCertificateStore();
  const { executePost: executePostCategory, loading: isSavingCategory } = useApi();
  const { executeGet: executeGetCategory, executeDelete: executeDeleteCategory, loading: isLoadingCategory } = useApi();
  const { executePost: executePostDetail, loading: isSavingDetail } = useApi();
  const { executeGet: executeGetDetail, executeDelete: executeDeleteDetail, loading: isLoadingDetail } = useApi();

  // ── Product Category state ───────────────────────────────────────────────
  const [categoryData, setCategoryData] = useState<IProductCategoryInformation[]>([]);
  const [categoryTableData, setCategoryTableData] = useState<IProductCategoryInformation[]>([]);
  const [categoryEditingRowIndex, setCategoryEditingRowIndex] = useState<number>(-1);
  const [categoryDeletingRowIndex, setCategoryDeletingRowIndex] = useState<number>(-1);
  const [productCategoryCodeOptions, setProductCategoryCodeOptions] = useState<OptionType[]>([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryPopUpTitle, setCategoryPopUpTitle] = useState("");
  const [categoryPopUpMessage, setCategoryPopUpMessage] = useState("");
  const [categoryPopUpBtnName, setCategoryPopUpBtnName] = useState("");

  // ── Product Detail state ─────────────────────────────────────────────────
  const [detailData, setDetailData] = useState<IProductDetailInformation[]>([]);
  const [detailTableData, setDetailTableData] = useState<IProductDetailInformation[]>([]);
  const [detailEditingRowIndex, setDetailEditingRowIndex] = useState<number>(-1);
  const [detailDeletingRowIndex, setDetailDeletingRowIndex] = useState<number>(-1);
  const [productDetailCodeOptions, setProductDetailCodeOptions] = useState<OptionType[]>([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailPopUpTitle, setDetailPopUpTitle] = useState("");
  const [detailPopUpMessage, setDetailPopUpMessage] = useState("");
  const [detailPopUpBtnName, setDetailPopUpBtnName] = useState("");

  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // ── Formik instances ─────────────────────────────────────────────────────
  const categoryFormik = useFormik<IProductCategoryInformation>({
    initialValues: initialProductCategoryInformation,
    validationSchema: buildProductCategoryValidationSchema(),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  const detailFormik = useFormik<IProductDetailInformation>({
    initialValues: initialProductDetailInformation,
    validationSchema: buildProductDetailValidationSchema(),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  useScrollToError(categoryFormik);
  useScrollToError(detailFormik);

  // ── Data fetching ────────────────────────────────────────────────────────
  const getProductCategoryCodes = async () => {
    try {
      const result: any = await dtrackItServiceV4.getProductCategory();
      const options = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
        code: t.code_1,
      }));
      const uniqueOptions = options.filter((option: any, index: number, self: any[]) =>
        index === self.findIndex((o: any) => o.value === option.value)
      );
      setProductCategoryCodeOptions(uniqueOptions);
    } catch (err) {
      Utills.cl(`Error in Fetching Product Category Codes ${err}`);
      notify('Error fetching product category codes', 'error');
    }
  };

  const getProductCategoryInformation = async () => {
    if (!SC_ID) return;
    try {
      const response = await executeGetCategory(API_ROUTES.GET_PRODUCT_CATEGORY_INFORMATION_V4, { scId: SC_ID });
      if (response?.status === 200 && response.data) {
        const apiData: IProductCategoryApiResponse = response.data;
        if (apiData.response && apiData.response.length > 0) {
          const transformedData: IProductCategoryInformation[] = apiData.response.map((item: any) => ({
            productCategoryId: item.productCategoryId,
            scProductCategoryCode: item.scProductCategoryCode || "",
            scProductCategoryUserSpecificTerm: item.scProductCategoryUserSpecificTerm || "",
            scProductCategoryCodeDescription: item.scProductCategoryCodeDescription || "",
          }));
          setCategoryData(transformedData);
          setCategoryTableData(transformedData);
        } else {
          setCategoryData([]);
          setCategoryTableData([]);
        }
      }
    } catch (error) {
      console.error('Error fetching product category information:', error);
      notify('Error fetching product category information', 'error');
    }
  };

  const getProductDetailCodes = async () => {
    try {
      const result: any = await dtrackItServiceV4.getProductDetailsCode();
      const options = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
        code: t.code_1,
      }));
      const uniqueOptions = options.filter(
        (option: any, index: number, self: any[]) =>
          index === self.findIndex((o: any) => o.value === option.value)
      );
      setProductDetailCodeOptions(uniqueOptions);
    } catch (err) {
      Utills.cl(`Error in Fetching Product Detail Codes ${err}`);
      notify("Error fetching product detail codes", "error");
    }
  };

  const getProductDetailInformation = async () => {
    if (!SC_ID) return;
    try {
      const response = await executeGetDetail(API_ROUTES.GET_PRODUCT_DETAIL_INFORMATION_V4, { scId: SC_ID });
      if (response?.status === 200 && response.data) {
        const apiData: IProductDetailApiResponse = response.data;
        if (apiData.response && apiData.response.length > 0) {
          const transformedData: IProductDetailInformation[] = apiData.response.map((item: any) => ({
            productDetailsId: item.productDetailsId,
            scProductDetailCode: item.scProductDetailCode || "",
            scProductDetailCodeDescription: item.scProductDetailCodeDescription || "",
            scProductDetailUserSpecificTerm: item.scProductDetailUserSpecificTerm || "",
          }));
          setDetailData(transformedData);
          setDetailTableData(transformedData);
        } else {
          setDetailData([]);
          setDetailTableData([]);
        }
      }
    } catch (error) {
      console.error("Error fetching product detail information:", error);
      notify("Error fetching product detail information", "error");
    }
  };

  useEffect(() => {
    const init = async () => {
      setIsInitialLoading(true);
      try {
        await Promise.all([
          getProductCategoryCodes(),
          getProductDetailCodes(),
          getProductCategoryInformation(),
          getProductDetailInformation(),
        ]);
      } finally {
        setIsInitialLoading(false);
      }
    };
    init();
  }, [SC_ID]);

  useEffect(() => {
    return () => {
      setRestrictPopUpSave(false);
    };
  }, []);

  // ── Category handlers ────────────────────────────────────────────────────
  // Strip trailing " (CODE)" from a label e.g. "Men's apparel (PC0001)" → "Men's apparel"
  const stripCode = (label: string) => label.replace(/\s*\([^)]+\)\s*$/, '').trim();

  const handleCategoryCellChange = (rowIndex: number, columnId: string, value: any) => {
    if (columnId === 'scProductCategoryCode') {
      categoryFormik.setFieldValue(columnId, value);
      const selectedOption = productCategoryCodeOptions.find(option => option.value === value);
      if (selectedOption) {
        const description = stripCode(selectedOption.label);
        categoryFormik.setFieldValue('scProductCategoryCodeDescription', description);
        categoryFormik.setFieldValue('scProductCategoryUserSpecificTerm', '');
        setCategoryData(prev =>
          prev.map((row, index) =>
            index === rowIndex
              ? { ...row, [columnId]: value, scProductCategoryCodeDescription: description, scProductCategoryUserSpecificTerm: '' }
              : row
          )
        );
      } else {
        setCategoryData(prev =>
          prev.map((row, index) => index === rowIndex ? { ...row, [columnId]: value } : row)
        );
      }
    } else {
      categoryFormik.setFieldValue(columnId, value);
      setCategoryData(prev =>
        prev.map((row, index) => index === rowIndex ? { ...row, [columnId]: value } : row)
      );
    }
  };

  const handleCategoryAddNewRow = () => {
    const newRow: IProductCategoryInformation = initialProductCategoryInformation;
    setRestrictPopUpSave(true);
    setCategoryData(prev => [newRow, ...prev]);
    setCategoryEditingRowIndex(0);
    categoryFormik.setValues(newRow);
    categoryFormik.setTouched({});
  };

  const handleCategoryCancelEdit = () => {
    setRestrictPopUpSave(false);
    setCategoryData(categoryTableData);
    setCategoryEditingRowIndex(-1);
    categoryFormik.resetForm();
  };

  const handleCategorySaveRow = async () => {
    if (categoryEditingRowIndex === null) {
      notify('No row selected for editing', 'error');
      return;
    }
    const currentRowData = categoryData[categoryEditingRowIndex];
    const { fieldErrors, generalError } = validateProductCategoryRow(currentRowData, categoryData, categoryEditingRowIndex);
    if (Object.keys(fieldErrors).length > 0) {
      const touchedMap: Record<string, boolean> = {};
      Object.entries(fieldErrors).forEach(([field, message]) => {
        categoryFormik.setFieldError(field, message);
        touchedMap[field] = true;
      });
      categoryFormik.setTouched(touchedMap);
      notify('Please fix the highlighted errors', 'error');
      categoryFormik.submitForm();
      return;
    }
    if (generalError) { notify(generalError, 'error'); return; }
    try {
      const updatedRow = {
        ...currentRowData,
        scProductCategoryUserSpecificTerm: (currentRowData.scProductCategoryUserSpecificTerm ?? "").trim(),
      };
      const existingRow = categoryData[categoryEditingRowIndex];
      const payload = mapProductCategoryPayload(updatedRow, SC_ID!);
      if (existingRow.productCategoryId) payload.productCategoryId = existingRow.productCategoryId;
      const response = await executePostCategory(API_ROUTES.POST_PRODUCT_CATEGORY_INFORMATION_V4, payload);
      if (response?.status === 200 || response?.status === 201) {
        const newData = categoryData.map((row, index) =>
          index === categoryEditingRowIndex ? { ...updatedRow, productCategoryId: existingRow.productCategoryId } : row
        );
        setCategoryData(newData);
        setCategoryTableData(newData);
        setRestrictPopUpSave(false);
        setCategoryEditingRowIndex(-1);
        categoryFormik.resetForm();
        await getProductCategoryInformation();
        notify('Product category information saved successfully', 'success');
        if (onNextTab) await onNextTab();
      } else {
        notify('Error saving product category information', 'error');
      }
    } catch (error) {
      console.error('Error saving product category information:', error);
      notify('Error saving product category information', 'error');
    }
  };

  const handleCategoryEditRow = (rowIndex: number) => {
    setRestrictPopUpSave(true);
    setCategoryEditingRowIndex(rowIndex);
    const rowData = categoryData[rowIndex];
    categoryFormik.setValues({
      scProductCategoryCode: rowData.scProductCategoryCode || "",
      scProductCategoryCodeDescription: rowData.scProductCategoryCodeDescription || "",
      scProductCategoryUserSpecificTerm: rowData.scProductCategoryUserSpecificTerm || "",
    });
  };

  const handleCategoryDeleteRow = (rowIndex: number) => {
    const rowToDelete = categoryData[rowIndex];
    if (rowToDelete.productCategoryId) {
      setCategoryPopUpTitle("Confirm Delete");
      setCategoryPopUpMessage("Are you sure you want to delete this product category? This action cannot be undone.");
      setCategoryPopUpBtnName("Delete");
      setCategoryDialogOpen(true);
      setCategoryDeletingRowIndex(rowIndex);
    } else {
      const updatedData = categoryData.filter((_, index) => index !== rowIndex);
      setCategoryData(updatedData);
      setCategoryTableData(updatedData);
    }
  };

  const handleCategoryConfirmDelete = async () => {
    try {
      const rowIndex = categoryDeletingRowIndex;
      const rowToDelete = categoryData[rowIndex];
      if (rowToDelete.productCategoryId) {
        const response = await executeDeleteCategory(API_ROUTES.DELETE_API_V4, {
          scId: SC_ID,
          productCategoryId: rowToDelete.productCategoryId,
        });
        if (response?.status === 200) {
          const updatedData = categoryData.filter((_, index) => index !== rowIndex);
          setCategoryData(updatedData);
          setCategoryTableData(updatedData);
          await getProductCategoryInformation();
          notify('Product category deleted successfully', 'success');
          if (onNextTab) await onNextTab();
        } else {
          notify('Failed to delete product category', 'error');
        }
      }
    } catch (error) {
      console.error('Error deleting product category:', error);
      notify('Error deleting product category', 'error');
    } finally {
      setCategoryDialogOpen(false);
      setCategoryDeletingRowIndex(-1);
    }
  };

  // ── Detail handlers ──────────────────────────────────────────────────────
  const handleDetailCellChange = (rowIndex: number, columnId: string, value: any) => {
    if (columnId === 'scProductDetailCode') {
      detailFormik.setFieldValue(columnId, value);
      const selectedOption = productDetailCodeOptions.find(option => option.value === value);
      if (selectedOption) {
        const description = stripCode(selectedOption.label);
        detailFormik.setFieldValue('scProductDetailCodeDescription', description);
        detailFormik.setFieldValue('scProductDetailUserSpecificTerm', '');
        setDetailData(prev =>
          prev.map((row, index) =>
            index === rowIndex
              ? { ...row, [columnId]: value, scProductDetailCodeDescription: description, scProductDetailUserSpecificTerm: '' }
              : row
          )
        );
      } else {
        setDetailData(prev =>
          prev.map((row, index) => index === rowIndex ? { ...row, [columnId]: value } : row)
        );
      }
    } else {
      detailFormik.setFieldValue(columnId, value);
      setDetailData(prev =>
        prev.map((row, index) => index === rowIndex ? { ...row, [columnId]: value } : row)
      );
    }
  };

  const handleDetailAddNewRow = () => {
    const newRow: IProductDetailInformation = initialProductDetailInformation;
    setRestrictPopUpSave(true);
    setDetailData(prev => [newRow, ...prev]);
    setDetailEditingRowIndex(0);
    detailFormik.setValues(newRow);
    detailFormik.setTouched({});
  };

  const handleDetailCancelEdit = () => {
    setRestrictPopUpSave(false);
    setDetailData(detailTableData);
    setDetailEditingRowIndex(-1);
    detailFormik.resetForm();
  };

  const handleDetailSaveRow = async () => {
    if (detailEditingRowIndex === null) {
      notify("No row selected for editing", "error");
      return;
    }
    const currentRowData = detailData[detailEditingRowIndex];
    const { fieldErrors, generalError } = validateProductDetailRow(currentRowData, detailData, detailEditingRowIndex);
    if (Object.keys(fieldErrors).length > 0) {
      const touchedMap: Record<string, boolean> = {};
      Object.entries(fieldErrors).forEach(([field, message]) => {
        detailFormik.setFieldError(field, message);
        touchedMap[field] = true;
      });
      detailFormik.setTouched(touchedMap);
      notify("Please fix the highlighted errors", "error");
      detailFormik.submitForm();
      return;
    }
    if (generalError) { notify(generalError, "error"); return; }
    try {
      const updatedRow = currentRowData;
      const existingRow = detailData[detailEditingRowIndex];
      const payload = mapProductDetailPayload(updatedRow, SC_ID!);
      if (existingRow.productDetailsId) payload.productDetailsId = existingRow.productDetailsId;
      const response = await executePostDetail(API_ROUTES.POST_PRODUCT_DETAIL_INFORMATION_V4, payload);
      if (response?.status === 200 || response?.status === 201) {
        const newData = detailData.map((row, index) =>
          index === detailEditingRowIndex ? { ...updatedRow, productDetailsId: existingRow.productDetailsId } : row
        );
        setDetailData(newData);
        setDetailTableData(newData);
        setRestrictPopUpSave(false);
        setDetailEditingRowIndex(-1);
        detailFormik.resetForm();
        await getProductDetailInformation();
        notify("Product detail information saved successfully", "success");
        if (onNextTab) await onNextTab();
      } else {
        notify("Error saving product detail information", "error");
      }
    } catch (error) {
      console.error("Error saving product detail information:", error);
      notify("Error saving product detail information", "error");
    }
  };

  const handleDetailEditRow = (rowIndex: number) => {
    setRestrictPopUpSave(true);
    setDetailEditingRowIndex(rowIndex);
    const rowData = detailData[rowIndex];
    detailFormik.setValues({
      scProductDetailCode: rowData.scProductDetailCode || "",
      scProductDetailCodeDescription: rowData.scProductDetailCodeDescription || "",
      scProductDetailUserSpecificTerm: rowData.scProductDetailUserSpecificTerm || "",
    });
  };

  const handleDetailDeleteRow = (rowIndex: number) => {
    const rowToDelete = detailData[rowIndex];
    if (rowToDelete.productDetailsId) {
      setDetailPopUpTitle("Confirm Delete");
      setDetailPopUpMessage("Are you sure you want to delete this product detail? This action cannot be undone.");
      setDetailPopUpBtnName("Delete");
      setDetailDialogOpen(true);
      setDetailDeletingRowIndex(rowIndex);
    } else {
      const updatedData = detailData.filter((_, index) => index !== rowIndex);
      setDetailData(updatedData);
      setDetailTableData(updatedData);
    }
  };

  const handleDetailConfirmDelete = async () => {
    try {
      const rowIndex = detailDeletingRowIndex;
      const rowToDelete = detailData[rowIndex];
      if (rowToDelete.productDetailsId) {
        const response = await executeDeleteDetail(API_ROUTES.DELETE_API_V4, {
          scId: SC_ID,
          productDetailsId: rowToDelete.productDetailsId,
        });
        if (response?.status === 200) {
          const updatedData = detailData.filter((_, index) => index !== rowIndex);
          setDetailData(updatedData);
          setDetailTableData(updatedData);
          await getProductDetailInformation();
          notify("Product detail deleted successfully", "success");
          if (onNextTab) await onNextTab();
        } else {
          notify("Failed to delete product detail", "error");
        }
      }
    } catch (error) {
      console.error("Error deleting product detail:", error);
      notify("Error deleting product detail", "error");
    } finally {
      setDetailDialogOpen(false);
      setDetailDeletingRowIndex(-1);
    }
  };

  // ── Navigation ───────────────────────────────────────────────────────────
  const handleNext = async () => {
    if (categoryData.length === 0) {
      notify('Please add at least one Product Category', 'error');
      return;
    }
    if (detailData.length === 0) {
      notify('Please add at least one Product Detail', 'error');
      return;
    }
    if (onNextTab) await onNextTab();
    navigate(`/v4/sc/${formtype}/raw-material-details/${SC_ID}`);
  };

  // ── Column definitions ───────────────────────────────────────────────────
  const categoryColumns: EditableColumnDef<IProductCategoryInformation>[] = [
    {
      accessorKey: "scProductCategoryCode",
      header: "Product Category Code",
      Header: <span>Product Category Code</span>,
      size: 185,
      minSize: 165,
      maxSize: 220,
      grow: false,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="scProductCategoryCode"
          value={value}
          placeholder="Select Product Category Code"
          options={productCategoryCodeOptions}
          enableSearch={true}
          size="small"
          tooltipError={true}
          showCodeOnlyWhenSelected={true}
          error={categoryFormik.touched.scProductCategoryCode && Boolean(categoryFormik.errors.scProductCategoryCode)}
          helperText={categoryFormik.touched.scProductCategoryCode && categoryFormik.errors.scProductCategoryCode ? (categoryFormik.errors.scProductCategoryCode as string) : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      ),
    },
    {
      accessorKey: "scProductCategoryCodeDescription",
      header: "Description",
      Header: <span>Description</span>,
      size: 200,
      minSize: 160,
      maxSize: 300,
      grow: true,
    },
    {
      accessorKey: "scProductCategoryUserSpecificTerm",
      header: "User Specific Term",
      Header: <span>User Specific Term</span>,
      size: 240,
      minSize: 150,
      maxSize: 300,
      grow: false,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="scProductCategoryUserSpecificTerm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Product Category User Specific Term"
          tooltipError={true}
          error={categoryFormik.touched.scProductCategoryUserSpecificTerm && Boolean(categoryFormik.errors.scProductCategoryUserSpecificTerm)}
          helperText={categoryFormik.touched.scProductCategoryUserSpecificTerm && categoryFormik.errors.scProductCategoryUserSpecificTerm ? (categoryFormik.errors.scProductCategoryUserSpecificTerm as string) : undefined}
        />
      ),
    },
  ];

  const detailColumns: EditableColumnDef<IProductDetailInformation>[] = [
    {
      accessorKey: "scProductDetailCode",
      header: "Product Detail Code",
      Header: (
        <span>
          <i style={{ color: "red" }}>* </i>Product Detail Code
        </span>
      ),
      size: 160,
      minSize: 140,
      maxSize: 200,
      grow: false,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="scProductDetailCode"
          value={value}
          placeholder="Select Product Detail Code"
          options={productDetailCodeOptions}
          enableSearch={true}
          size="small"
          tooltipError={true}
          showCodeOnlyWhenSelected={true}
          error={detailFormik.touched.scProductDetailCode && Boolean(detailFormik.errors.scProductDetailCode)}
          helperText={detailFormik.touched.scProductDetailCode && detailFormik.errors.scProductDetailCode ? (detailFormik.errors.scProductDetailCode as string) : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      ),
    },
    {
      accessorKey: "scProductDetailCodeDescription",
      header: "Description",
      Header: <span>Description</span>,
      size: 200,
      minSize: 160,
      maxSize: 300,
      grow: true,
    },
    {
      accessorKey: "scProductDetailUserSpecificTerm",
      header: "User Specific Term",
      Header: <span>User Specific Term</span>,
      size: 160,
      minSize: 130,
      maxSize: 200,
      grow: false,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="scProductDetailUserSpecificTerm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Product Detail User Specific Term"
          tooltipError={true}
          error={detailFormik.touched.scProductDetailUserSpecificTerm && Boolean(detailFormik.errors.scProductDetailUserSpecificTerm)}
          helperText={detailFormik.touched.scProductDetailUserSpecificTerm && detailFormik.errors.scProductDetailUserSpecificTerm ? (detailFormik.errors.scProductDetailUserSpecificTerm as string) : undefined}
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  const isSaving = isSavingCategory || isSavingDetail;
  const isLoading = isLoadingCategory || isLoadingDetail;

  const actions: ActionButtonConfig[] = [
    {
      label: "Home",
      onClick: () => navigate('/v4/sc-dashboard'),
      variant: "contained",
      color: "default",
    },
    {
      label: "Back",
      onClick: () => navigate(`/v4/sc/${formtype}/facility-details/${SC_ID}`),
      variant: "contained" as const,
      color: "default" as const,
    },
    {
       label: "Next",
       onClick: handleNext,
       disabled: isRestrictPopUpSave || isSaving || categoryData.length === 0 || detailData.length === 0,
       variant: "contained",
       color: "primary",
     },
  ];

  return (
    <>
      {isInitialLoading ? (
        <LoaderComponent />
      ) : (
        <div>
          <Box component="main" sx={{ flexGrow: 1, p: 1, my: 4 }}>
            <Grid container spacing={2}>
              {/* ── Product Category Table ─────────────────────────────── */}
              <Grid item xs={12} md={6}>
                <Box display="flex" mb={2} justifyContent="flex-end" width="100%">
                  <StyledButton
                    onClick={handleCategoryAddNewRow}
                    sx={{ mb: 1 }}
                    disabled={
                      !categoryFormik.isValid ||
                      isReadOnlyBasedOnStatus ||
                      isRestrictPopUpSave ||
                      isSaving ||
                      isLoading ||
                      isInitialLoading
                    }
                  >
                    Add Product Category
                  </StyledButton>
                </Box>
                <EditableTable
                  data={categoryData}
                  columns={categoryColumns}
                  onSaveRow={handleCategorySaveRow}
                  onCellChange={handleCategoryCellChange}
                  editingRowIndex={categoryEditingRowIndex}
                  onEditRow={handleCategoryEditRow}
                  onCancelEdit={handleCategoryCancelEdit}
                  onDeleteRow={handleCategoryDeleteRow}
                  enableEditing={!isReadOnlyBasedOnStatus}
                  isSaving={isSavingCategory}
                />
              </Grid>

              {/* ── Product Detail Table ───────────────────────────────── */}
              <Grid item xs={12} md={6}>
                <Box display="flex" mb={2} justifyContent="flex-end" width="100%">
                  <StyledButton
                    onClick={handleDetailAddNewRow}
                    sx={{ mb: 1 }}
                    disabled={
                      !detailFormik.isValid ||
                      isReadOnlyBasedOnStatus ||
                      isRestrictPopUpSave ||
                      isSaving ||
                      isLoading ||
                      isInitialLoading
                    }
                  >
                    Add Product Details
                  </StyledButton>
                </Box>
                <EditableTable
                  data={detailData}
                  columns={detailColumns}
                  onSaveRow={handleDetailSaveRow}
                  onCellChange={handleDetailCellChange}
                  editingRowIndex={detailEditingRowIndex}
                  onEditRow={handleDetailEditRow}
                  onCancelEdit={handleDetailCancelEdit}
                  onDeleteRow={handleDetailDeleteRow}
                  enableEditing={!isReadOnlyBasedOnStatus}
                  isSaving={isSavingDetail}
                />
              </Grid>
            </Grid>
          </Box>

          <div style={{ marginTop: "3rem" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              {actions.map((action, index) => (
                <StyledButton
                  key={index}
                  onClick={action.onClick}
                  variant={action.variant || "text"}
                  disabled={action.disabled}
                >
                  {action.label}
                </StyledButton>
              ))}
            </Box>
          </div>

          {/* Category delete confirmation */}
          <AlertPopup
            open={categoryDialogOpen}
            title={categoryPopUpTitle}
            message={categoryPopUpMessage}
            onClose={() => setCategoryDialogOpen(false)}
            onConfirm={handleCategoryConfirmDelete}
            confirmText={categoryPopUpBtnName}
            cancelText="Cancel"
          />

          {/* Detail delete confirmation */}
          <AlertPopup
            open={detailDialogOpen}
            title={detailPopUpTitle}
            message={detailPopUpMessage}
            onClose={() => setDetailDialogOpen(false)}
            onConfirm={handleDetailConfirmDelete}
            confirmText={detailPopUpBtnName}
            cancelText="Cancel"
          />
        </div>
      )}
    </>
  );
};

export default ProductCategoryInformationTab;
